import { useReactiveVar } from '@apollo/client';
import { searchComboboxDefaultFilter } from '@synoptic/ui-kit/search-combobox/search-combobox-default-filter.js';
import { SearchComboboxEmpty } from '@synoptic/ui-kit/search-combobox/search-combobox-empty.js';
import { SearchComboboxInput } from '@synoptic/ui-kit/search-combobox/search-combobox-input.js';
import { SearchComboboxItem } from '@synoptic/ui-kit/search-combobox/search-combobox-item.js';
import { SearchComboboxList } from '@synoptic/ui-kit/search-combobox/search-combobox-list.js';
import { SearchComboboxRoot } from '@synoptic/ui-kit/search-combobox/search-combobox.js';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { EventsMapping } from 'sigma/types';
import { NodeDataMini } from '@/services/twttr-graph-service';
import { sigmaInstanceVar } from './sigma-options-var';

const filterData = (data: NodeDataMini[], searchQuery: string) => {
  const limit = 30;

  const resWithScore: [NodeDataMini, score: number][] = [];

  for (const node of data) {
    const value = node.screen_name;
    const keywords = [node.screen_name];
    const score = value
      ? searchComboboxDefaultFilter(value, searchQuery, keywords)
      : 0;

    if (score > 0) {
      resWithScore.push([node, score]);
    }
  }

  return resWithScore
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([node]) => node);
};

export const SigmaSearch = ({
  className,
  data,
}: {
  className?: string;
  data: NodeDataMini[];
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [, setSearchParams] = useSearchParams();

  // const [focusedNode, setFocusedNode] = useState<number | null>(null);

  const deferedQuery = useDeferredValue(searchQuery);

  // useEffect(() => {
  //   if (focusedNode) {
  //     const sigma = sigmaInstanceVar();
  //     if (!sigma) return;
  //     sigma.getGraph().setNodeAttribute(focusedNode, 'highlighted', true);

  //     return () => {
  //       sigma.getGraph().setNodeAttribute(focusedNode, 'highlighted', false);
  //     };
  //   }
  // }, [focusedNode]);

  const sigma = useReactiveVar(sigmaInstanceVar);

  const onSelectNode = (node: string) => {
    setSearchParams((prev) => {
      prev.set('node', node);
      return prev;
    });
  };

  useEffect(() => {
    if (sigma) {
      const resetSearch: EventsMapping['clickStage'] = () => {
        setSearchQuery('');
      };
      sigma.on('clickStage', resetSearch);
      sigma.on('clickNode', resetSearch);

      return () => {
        sigma.off('clickStage', resetSearch);
        sigma.off('clickNode', resetSearch);
      };
    }
  }, [sigma]);

  const filteredData = useMemo(
    () => (!deferedQuery ? data.slice(0, 20) : filterData(data, deferedQuery)),
    [data, deferedQuery],
  );

  return (
    <SearchComboboxRoot shouldFilter={false} className={className}>
      <SearchComboboxInput
        placeholder="Search user..."
        value={searchQuery}
        onValueChange={(value) => {
          setSearchQuery(value);
          // if (!value) setFocusedNode(null);
        }}
      />
      <SearchComboboxList inPopover>
        <SearchComboboxEmpty>
          User was not found in the graph
        </SearchComboboxEmpty>
        {filteredData.map((node) => (
          <SearchComboboxItem
            key={node.id}
            value={node.screen_name}
            onSelect={() => {
              const sigma = sigmaInstanceVar();
              if (!sigma) return;

              setSearchQuery(node.screen_name);
              onSelectNode(String(node.id));

              sigma.getCamera().animate(
                {
                  ...sigma.getNodeDisplayData(node.id)!,
                  ratio: 0.2,
                },
                { duration: 300 },
              );
            }}
          >
            {node.screen_name}
          </SearchComboboxItem>
        ))}
      </SearchComboboxList>
    </SearchComboboxRoot>
  );
};
